import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  setDoc,
} from 'firebase/firestore';
import { db } from './config';

const getBlogsCollection = (clientId) =>
  collection(db, 'clients', clientId, 'blogs');

// ─── ADMIN OPERATIONS ────────────────────────────────────────────────────────

export const createBlog = async (clientId, blogData) => {
  const col = getBlogsCollection(clientId);
  const docRef = await addDoc(col, {
    ...blogData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: blogData.status === 'published' ? serverTimestamp() : null,
    views: 0,
  });
  return docRef.id;
};

export const updateBlog = async (clientId, blogId, blogData) => {
  const docRef = doc(db, 'clients', clientId, 'blogs', blogId);
  const updateData = {
    ...blogData,
    updatedAt: serverTimestamp(),
  };
  if (blogData.status === 'published' && blogData._setPublishedAt) {
    updateData.publishedAt = serverTimestamp();
  }
  delete updateData._setPublishedAt;
  await updateDoc(docRef, updateData);
};

export const deleteBlog = async (clientId, blogId) => {
  const docRef = doc(db, 'clients', clientId, 'blogs', blogId);
  await deleteDoc(docRef);
};

export const getAllBlogs = async (clientId) => {
  const col = getBlogsCollection(clientId);
  const q = query(col, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getBlogById = async (clientId, blogId) => {
  const docRef = doc(db, 'clients', clientId, 'blogs', blogId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() };
};

export const getAdminStats = async (clientId) => {
  const col = getBlogsCollection(clientId);
  const snapshot = await getDocs(col);
  const blogs = snapshot.docs.map((d) => d.data());
  return {
    total: blogs.length,
    published: blogs.filter((b) => b.status === 'published').length,
    drafts: blogs.filter((b) => b.status === 'draft').length,
    totalViews: blogs.reduce((sum, b) => sum + (b.views || 0), 0),
  };
};

// ─── USER / PUBLIC OPERATIONS ─────────────────────────────────────────────────

export const getPublishedBlogs = async (clientId, limitCount = 50) => {
  const col = getBlogsCollection(clientId);
  const q = query(
    col,
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getBlogBySlug = async (clientId, slug) => {
  const col = getBlogsCollection(clientId);
  const q = query(
    col,
    where('slug', '==', slug),
    where('status', '==', 'published')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, ...d.data() };
};

export const getBlogsByCategory = async (clientId, category) => {
  const col = getBlogsCollection(clientId);
  const q = query(
    col,
    where('status', '==', 'published'),
    where('category', '==', category),
    orderBy('publishedAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getFeaturedBlogs = async (clientId, limitCount = 3) => {
  const col = getBlogsCollection(clientId);
  const q = query(
    col,
    where('status', '==', 'published'),
    where('featured', '==', true),
    orderBy('publishedAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const incrementViews = async (clientId, blogId) => {
  const docRef = doc(db, 'clients', clientId, 'blogs', blogId);
  await updateDoc(docRef, { views: increment(1) });
};

// ─── CLIENT SETTINGS ─────────────────────────────────────────────────────────

export const getClientSettings = async (clientId) => {
  const docRef = doc(db, 'clients', clientId, 'settings', 'general');
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return snapshot.data();
};

export const saveClientSettings = async (clientId, settings) => {
  const docRef = doc(db, 'clients', clientId, 'settings', 'general');
  await setDoc(docRef, settings, { merge: true });
};
